import { Component, Input, ChangeDetectorRef, inject } from '@angular/core';
import { LogError, RunView } from '@memberjunction/core';
import {
    marketingBlogEntity,
    marketingBlogRevisionEntity,
    marketingContentStatusEntity,
    marketingContentTypeEntity,
} from 'mj_generatedentities';
import { PerformanceMetricRead } from '../../marketing-workspace.component';

type BlogFilter = 'needs-review' | 'ready' | 'published' | 'all';

interface BlogStat {
    label: string;
    value: number | string;
    sub?: string;
    filter?: BlogFilter;
    alert?: boolean;
}

/**
 * Programs / Blog — podcast-to-blog pipeline view.
 *
 * Composes existing <mkt-review-queue> (which has cards + resizable side panel built-in)
 * for the editorial-grade UX. KPI strip + filter pills on top, queue + side panel below.
 *
 * State flows down from the workspace shell. Save handlers update entities directly
 * — no duplication of legacy CMO Dashboard logic.
 */
@Component({
    standalone: false,
    selector: 'mkt-ws-programs-blog',
    template: `
<mkt-ws-page-head
    [eyebrow]="'Programs · Blog' + (companyName ? ' · ' + companyName : '')"
    title="Blog program."
    sub="Podcast-to-blog pipeline. Each episode produces a transcript blog plus topic blogs. Review drafts before they publish to HubSpot.">
</mkt-ws-page-head>

<div *ngIf="isLoadingContent" class="pb__loading">
    <i class="fa-solid fa-spinner fa-spin"></i> Loading blogs…
</div>

<ng-container *ngIf="!isLoadingContent">
    <!-- Metric strip -->
    <div class="pb__metrics">
        <div *ngFor="let m of stats"
             class="pb__metric"
             [class.pb__metric--clickable]="m.filter"
             [class.pb__metric--alert]="m.alert"
             (click)="m.filter && setFilter(m.filter)">
            <div class="pb__metric-label">{{ m.label }}</div>
            <div class="pb__metric-value">{{ m.value }}</div>
            <div *ngIf="m.sub" class="pb__metric-sub">{{ m.sub }}</div>
        </div>
    </div>

    <!-- Filter pills -->
    <div class="pb__pills">
        <button class="pb__pill" [class.pb__pill--active]="filter === 'needs-review'" (click)="setFilter('needs-review')">
            Needs review · {{ counts.needsReview }}
        </button>
        <button class="pb__pill" [class.pb__pill--active]="filter === 'ready'" (click)="setFilter('ready')">
            Ready / scheduled · {{ counts.ready }}
        </button>
        <button class="pb__pill" [class.pb__pill--active]="filter === 'published'" (click)="setFilter('published')">
            Published · {{ counts.published }}
        </button>
        <button class="pb__pill" [class.pb__pill--active]="filter === 'all'" (click)="setFilter('all')">
            All · {{ blogs.length }}
        </button>
    </div>

    <!-- Existing review queue handles the cards + resizable side panel + revision panel. -->
    <mkt-review-queue
        *ngIf="filteredBlogs.length > 0; else emptyState"
        [blogs]="filteredBlogs"
        [selectedBlogId]="selectedBlogId"
        [selectedBlog]="selectedBlog"
        [allRevisions]="revisions"
        [brandVoiceScores]="brandVoiceScores"
        (selectBlog)="onSelectBlog($event)"
        (closeBlogDetail)="onCloseBlogDetail()"
        (statusChange)="onStatusChange($event)"
        (deleteBlog)="onDeleteBlog($event)"
        (blogSaved)="onBlogSaved()">
    </mkt-review-queue>

    <ng-template #emptyState>
        <mkt-ws-coming-soon
            icon="fa-file-circle-check"
            [title]="emptyTitle"
            [body]="emptyBody">
        </mkt-ws-coming-soon>
    </ng-template>
</ng-container>
    `,
    styles: [`
        :host { display: block; }
        .pb__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }

        .pb__metrics { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
        .pb__metric { background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 10px; padding: 14px 16px; }
        .pb__metric--clickable { cursor: pointer; transition: border-color 0.15s; }
        .pb__metric--clickable:hover { border-color: var(--mj-brand-primary, #6366f1); }
        .pb__metric--alert .pb__metric-value { color: var(--mj-status-warning, #b45309); }
        .pb__metric-label { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); margin-bottom: 8px; }
        .pb__metric-value { font-size: 22px; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); font-variant-numeric: tabular-nums; line-height: 1.1; }
        .pb__metric-sub { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); margin-top: 4px; }

        .pb__pills { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); }
        .pb__pill { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 99px; background: var(--mj-bg-surface, #fff); color: var(--mj-text-secondary, #71717a); font-size: 12px; font-weight: 500; font-family: inherit; cursor: pointer; transition: filter 0.15s, border-color 0.15s, color 0.15s; }
        .pb__pill:hover { color: var(--mj-text-primary, #1a1a1a); border-color: var(--mj-brand-primary, #6366f1); }
        .pb__pill--active { background: var(--mj-brand-primary, #6366f1); color: var(--mj-text-inverse, #fff); border-color: var(--mj-brand-primary, #6366f1); }

        @media (max-width: 1100px) { .pb__metrics { grid-template-columns: repeat(2, 1fr); } }
    `]
})
export class ProgramsBlogSection {
    @Input() companyId = '';
    @Input() companyName = '';
    @Input() blogs: marketingBlogEntity[] = [];
    @Input() contentStatuses: marketingContentStatusEntity[] = [];
    @Input() contentTypes: marketingContentTypeEntity[] = [];
    @Input() performanceMetrics: PerformanceMetricRead[] = [];
    @Input() isLoadingContent = false;
    @Input() navigate: (path: string) => void = () => {};

    private cdr = inject(ChangeDetectorRef);

    filter: BlogFilter = 'needs-review';
    selectedBlogId: string | null = null;
    selectedBlog: marketingBlogEntity | null = null;
    revisions: marketingBlogRevisionEntity[] = [];
    brandVoiceScores: Record<string, number> = {};

    get counts() {
        return {
            needsReview: this.blogs.filter(b => this.matches(b, 'needs-review')).length,
            ready: this.blogs.filter(b => this.matches(b, 'ready')).length,
            published: this.blogs.filter(b => this.matches(b, 'published')).length,
        };
    }

    get filteredBlogs(): marketingBlogEntity[] {
        return this.filter === 'all' ? this.blogs : this.blogs.filter(b => this.matches(b, this.filter));
    }

    get stats(): BlogStat[] {
        const c = this.counts;
        const thisMonth = this.blogs.filter(b => this.isCurrentMonth(b.Get('__mj_CreatedAt'))).length;
        const ga4 = this.ga4PageviewStat();
        return [
            { label: 'Needs review', value: c.needsReview, filter: 'needs-review', alert: c.needsReview > 0 },
            { label: 'Ready', value: c.ready, filter: 'ready' },
            { label: 'Published', value: c.published, filter: 'published' },
            { label: 'Created this month', value: thisMonth, filter: 'all' },
            ga4,
        ];
    }

    /**
     * "Avg 7-day pageviews" — mean PageViews per (blog, day) over last 7 GA4 days,
     * with prior-7-day baseline for the +/- delta. Reads `performanceMetrics`
     * (last 14d, GA4-only after filter). Falls back to a "no data yet" tile
     * if GA4 hasn't been pulled yet for this company.
     */
    private ga4PageviewStat(): BlogStat {
        const ga4 = this.performanceMetrics.filter(m => m.Platform === 'GA4' && m.PageViews != null);
        if (ga4.length === 0) {
            return { label: 'Avg 7-day pageviews', value: '—', sub: 'GA4 not yet pulled' };
        }
        const today = new Date(); today.setUTCHours(0, 0, 0, 0);
        const sevenAgo = new Date(today); sevenAgo.setUTCDate(sevenAgo.getUTCDate() - 7);
        const fourteenAgo = new Date(today); fourteenAgo.setUTCDate(fourteenAgo.getUTCDate() - 14);

        const inWindow = (m: PerformanceMetricRead, from: Date, to: Date) => {
            const d = new Date(m.MetricDate as unknown as string);
            return d >= from && d < to;
        };
        const mean = (rows: PerformanceMetricRead[]) =>
            rows.length === 0 ? 0 : rows.reduce((s, r) => s + (r.PageViews ?? 0), 0) / rows.length;

        const recent = ga4.filter(m => inWindow(m, sevenAgo, today));
        const baseline = ga4.filter(m => inWindow(m, fourteenAgo, sevenAgo));
        const recentMean = mean(recent);
        const baselineMean = mean(baseline);

        const value = Math.round(recentMean);
        let sub = 'From GA4';
        if (baseline.length > 0 && baselineMean > 0) {
            const pct = Math.round(((recentMean - baselineMean) / baselineMean) * 100);
            const sign = pct >= 0 ? '+' : '';
            sub = `From GA4 · ${sign}${pct}% vs prior 7d`;
        }
        return { label: 'Avg 7-day pageviews', value, sub };
    }

    get emptyTitle(): string {
        switch (this.filter) {
            case 'needs-review': return 'Nothing to review';
            case 'ready': return 'Nothing scheduled';
            case 'published': return 'No published blogs yet';
            default: return 'No blogs yet';
        }
    }
    get emptyBody(): string {
        return this.filter === 'all'
            ? 'When the agent drafts blogs from podcast episodes they\'ll appear here.'
            : `Switch to "All" to see every blog regardless of status.`;
    }

    setFilter(f: BlogFilter): void {
        this.filter = f;
        this.selectedBlogId = null;
        this.selectedBlog = null;
        this.cdr.detectChanges();
    }

    onSelectBlog(blogId: string): void {
        this.selectedBlogId = blogId;
        this.selectedBlog = this.blogs.find(b => b.ID === blogId) ?? null;
        this.cdr.detectChanges();
    }

    onCloseBlogDetail(): void {
        this.selectedBlogId = null;
        this.selectedBlog = null;
        this.cdr.detectChanges();
    }

    async onStatusChange(event: { blogId: string; newStatus: string }): Promise<void> {
        const blog = this.blogs.find(b => b.ID === event.blogId);
        if (!blog) return;
        try {
            // Blog uses BlogStatus (StatusID -> Blog Status). Resolve target via lookup view.
            const rv = new RunView();
            const result = await rv.RunView<{ ID: string }>({
                EntityName: 'Blog Status',
                ExtraFilter: `Name='${event.newStatus.replace(/'/g, "''")}'`,
                MaxRows: 1, ResultType: 'simple', Fields: ['ID'],
            });
            if (!result.Success || result.Results.length === 0) {
                LogError(`[ProgramsBlogSection] Blog Status lookup failed for '${event.newStatus}': ${result.ErrorMessage || 'no rows'}`);
                return;
            }
            blog.Set('StatusID', result.Results[0].ID);
            const saved = await blog.Save();
            if (!saved) {
                LogError(`[ProgramsBlogSection] blog.Save() failed for blog ${blog.ID}: ${blog.LatestResult?.Message ?? 'unknown'}`);
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[ProgramsBlogSection] onStatusChange threw for blog ${event.blogId}: ${msg}`);
        } finally {
            this.cdr.detectChanges();
        }
    }

    async onDeleteBlog(blogId: string): Promise<void> {
        const blog = this.blogs.find(b => b.ID === blogId);
        if (!blog) return;
        try {
            if (await blog.Delete()) {
                this.onCloseBlogDetail();
            } else {
                LogError(`[ProgramsBlogSection] blog.Delete() failed for blog ${blogId}: ${blog.LatestResult?.Message ?? 'unknown'}`);
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[ProgramsBlogSection] onDeleteBlog threw for blog ${blogId}: ${msg}`);
        }
    }

    onBlogSaved(): void {
        this.cdr.detectChanges();
    }

    private matches(b: marketingBlogEntity, f: BlogFilter): boolean {
        const status = String(b.Get('Status') ?? '').toLowerCase();
        switch (f) {
            case 'needs-review': return status === 'draft' || status === 'review';
            case 'ready': return status === 'ready' || status === 'approved' || status === 'scheduled';
            case 'published': return status === 'published';
            case 'all': return true;
        }
    }

    private isCurrentMonth(d: Date | string | null | undefined): boolean {
        if (!d) return false;
        const dt = new Date(d);
        const now = new Date();
        return dt.getFullYear() === now.getFullYear() && dt.getMonth() === now.getMonth();
    }
}
